import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { CgTrash } from "react-icons/cg";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import DeleteConfirmModal from "../Commaon/DeleteConfirmModal";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import ListHeader from "../Commaon/ListHeader";
import ListPagination from "../Commaon/ListPagination";

const getRows = (data) => {
  if (Array.isArray(data?.r)) return data.r;
  if (Array.isArray(data?.r?.data)) return data.r.data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (value.$oid) return value.$oid;
    if (value._id) return normalizeId(value._id);
    if (value.userId) return normalizeId(value.userId);
    if (value.id) return normalizeId(value.id);
    if (typeof value.toString === "function") {
      const stringValue = value.toString();
      return stringValue === "[object Object]" ? "" : stringValue;
    }
  }
  return String(value);
};

const NotificationsList = () => {
  const [notifications, setNotifications] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(25);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteIds, setDeleteIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [deleteGroupId, setDeleteGroupId] = useState(null);
  const [promoModal, setPromoModal] = useState(false);
  const [sendModal, setSendModal] = useState(false);
  const [groupModal, setGroupModal] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [promoForm, setPromoForm] = useState({ title: "", message: "" });
  const [sendForm, setSendForm] = useState({
    title: "",
    message: "",
    notificationType: 1,
    userType: 1,
    sentType: 1,
    messageType: 2,
    isStore: 1,
    userIds: [],
    groupIds: [],
  });
  const [groupForm, setGroupForm] = useState({ groupName: "", userIds: [] });
  const [sendUserDropdownOpen, setSendUserDropdownOpen] = useState(false);
  const [sendGroupDropdownOpen, setSendGroupDropdownOpen] = useState(false);
  const [groupUserDropdownOpen, setGroupUserDropdownOpen] = useState(false);
  const sendUserDropdownRef = useRef(null);
  const sendGroupDropdownRef = useRef(null);
  const groupUserDropdownRef = useRef(null);

  const showSnack = (message, type = "success") => {
    setSnack({ notification: { message, type } });
  };

  const totalPages = Math.ceil(totalRecords / limit) || 1;

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(
        `/admin/notification?notificationType=1&userType=1&page=${pageNo}&limit=${limit}`,
        { method: "GET" }
      );
      setNotifications(getRows(response.data));
      setTotalRecords(response?.data?.tr || 0);
    } catch (error) {
      showSnack("Error fetching notifications", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await AuthInterceptor("/admin/get-notification-group", {
        method: "GET",
      });
      setGroups(getRows(response.data));
    } catch (error) {
      setGroups([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await AuthInterceptor("/admin/get-userlist", {
        method: "GET",
      });
      setUsers(getRows(response.data));
    } catch (error) {
      setUsers([]);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, limit]);

  useEffect(() => {
    fetchGroups();
    fetchUsers();
  }, []);

  useEffect(() => {
    const closeDropdownsOnOutsideClick = (event) => {
      if (
        sendUserDropdownRef.current &&
        !sendUserDropdownRef.current.contains(event.target)
      ) {
        setSendUserDropdownOpen(false);
      }
      if (
        sendGroupDropdownRef.current &&
        !sendGroupDropdownRef.current.contains(event.target)
      ) {
        setSendGroupDropdownOpen(false);
      }
      if (
        groupUserDropdownRef.current &&
        !groupUserDropdownRef.current.contains(event.target)
      ) {
        setGroupUserDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", closeDropdownsOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeDropdownsOnOutsideClick);
  }, []);

  const filteredNotifications = useMemo(() => {
    if (!query.trim()) return notifications;
    const search = query.toLowerCase();
    return notifications.filter(
      (item) =>
        item.title?.toLowerCase().includes(search) ||
        item.message?.toLowerCase().includes(search)
    );
  }, [notifications, query]);
  const visibleIds = filteredNotifications.map((item) => item._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const getUserId = (user) => normalizeId(user?._id || user?.userId || user?.id || user);
  const getGroupId = (group) => normalizeId(group?._id || group?.id || group);
  const getUserLabel = (user) =>
    user?.fullName || user?.name || user?.email || user?.mobileNumber || getUserId(user) || "User";
  const getGroupLabel = (group) => group?.groupName || getGroupId(group) || "Group";
  const unselectedSendUsers = users.filter((user) => !sendForm.userIds.includes(getUserId(user)));
  const selectedSendUsers = sendForm.userIds.map((id) => {
    const user = users.find((item) => getUserId(item) === normalizeId(id));
    return { id, label: user ? getUserLabel(user) : id };
  });
  const unselectedSendGroups = groups.filter((group) => !sendForm.groupIds.includes(getGroupId(group)));
  const selectedSendGroups = sendForm.groupIds.map((id) => {
    const group = groups.find((item) => getGroupId(item) === normalizeId(id));
    return { id, label: group ? getGroupLabel(group) : id };
  });
  const unselectedGroupUsers = users.filter((user) => !groupForm.userIds.includes(getUserId(user)));
  const selectedGroupUsers = groupForm.userIds.map((id) => {
    const user = users.find((item) => getUserId(item) === normalizeId(id));
    return { id, label: user ? getUserLabel(user) : id };
  });

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  const addGroupUserId = (id) => {
    setGroupForm((prev) => ({
      ...prev,
      userIds: Array.from(new Set([...prev.userIds, id])),
    }));
  };

  const addSendUserId = (id) => {
    setSendForm((prev) => ({
      ...prev,
      userIds: Array.from(new Set([...prev.userIds, id])),
    }));
  };

  const removeSendUserId = (id) => {
    setSendForm((prev) => ({
      ...prev,
      userIds: prev.userIds.filter((userId) => userId !== id),
    }));
  };

  const addSendGroupId = (id) => {
    setSendForm((prev) => ({
      ...prev,
      groupIds: Array.from(new Set([...prev.groupIds, id])),
    }));
  };

  const removeSendGroupId = (id) => {
    setSendForm((prev) => ({
      ...prev,
      groupIds: prev.groupIds.filter((groupId) => groupId !== id),
    }));
  };

  const removeGroupUserId = (id) => {
    setGroupForm((prev) => ({
      ...prev,
      userIds: prev.userIds.filter((userId) => userId !== id),
    }));
  };

  const openDeleteNotifications = (ids) => {
    if (!ids.length) {
      showSnack("Select at least one notification", "error");
      return;
    }
    setDeleteId(null);
    setDeleteIds(ids);
  };

  const submitPromo = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await AuthInterceptor("/admin/notification-promo", {
        method: "POST",
        body: JSON.stringify(promoForm),
      });
      if ([200, 201].includes(response?.data?.s) || [200, 201].includes(response?.status)) {
        showSnack("Promo notification added successfully");
        setPromoModal(false);
        setPromoForm({ title: "", message: "" });
        fetchNotifications();
      } else {
        showSnack(response?.data?.m || "Error adding promo notification", "error");
      }
    } catch (error) {
      showSnack("Error adding promo notification", "error");
    } finally {
      setLoading(false);
    }
  };

  const submitNotification = async (event) => {
    event.preventDefault();
    const body = {
      title: sendForm.title,
      message: sendForm.message,
      notificationType: Number(sendForm.notificationType),
      userType: Number(sendForm.userType),
      sentType: Number(sendForm.sentType),
      messageType: Number(sendForm.messageType),
      isStore: Number(sendForm.isStore),
      payload: { message: sendForm.message },
    };
    if (sendForm.userIds.length) body.userIds = sendForm.userIds;
    if (sendForm.groupIds.length) body.GroupIds = sendForm.groupIds;

    setLoading(true);
    try {
      const response = await AuthInterceptor("/admin/sent-notification", {
        method: "POST",
        body: JSON.stringify(body),
      });
      if ([200, 201].includes(response?.data?.s) || [200, 201].includes(response?.status)) {
        showSnack("Notification sent successfully");
        setSendModal(false);
        setSendUserDropdownOpen(false);
        setSendGroupDropdownOpen(false);
        fetchNotifications();
      } else {
        showSnack(response?.data?.m || "Error sending notification", "error");
      }
    } catch (error) {
      showSnack("Error sending notification", "error");
    } finally {
      setLoading(false);
    }
  };

  const submitGroup = async (event) => {
    event.preventDefault();
    const body = {
      groupName: groupForm.groupName,
      userIds: groupForm.userIds,
    };
    setLoading(true);
    try {
      const response = await AuthInterceptor(
        editGroup ? `/admin/edit-notification-group/${editGroup._id}` : "/admin/notification-group",
        {
          method: editGroup ? "PATCH" : "POST",
          body: JSON.stringify(body),
        }
      );
      if ([200, 201].includes(response?.data?.s) || [200, 201].includes(response?.status)) {
        showSnack(`Group ${editGroup ? "updated" : "added"} successfully`);
        setGroupModal(false);
        setEditGroup(null);
        setGroupForm({ groupName: "", userIds: [] });
        setGroupUserDropdownOpen(false);
        fetchGroups();
      } else {
        showSnack(response?.data?.m || "Error saving group", "error");
      }
    } catch (error) {
      showSnack("Error saving group", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteNotification = async () => {
    const ids = deleteIds.length ? deleteIds : deleteId ? [deleteId] : [];
    if (!ids.length) return;

    setLoading(true);
    try {
      const response = await AuthInterceptor("/admin/notification", {
        method: "DELETE",
        body: JSON.stringify({ ids }),
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        showSnack(response?.data?.m || "Notification deleted successfully");
        setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
        fetchNotifications();
      }
    } catch (error) {
      showSnack("Error deleting notification", "error");
    } finally {
      setLoading(false);
      setDeleteId(null);
      setDeleteIds([]);
    }
  };

  const deleteGroup = async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(`/admin/delete-notification-group/${deleteGroupId}`, {
        method: "DELETE",
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        showSnack("Group deleted successfully");
        fetchGroups();
      }
    } catch (error) {
      showSnack("Error deleting group", "error");
    } finally {
      setLoading(false);
      setDeleteGroupId(null);
    }
  };

  const openGroupEdit = (group) => {
    const selectedUsers = [
      ...(Array.isArray(group.userIds) ? group.userIds : []),
      ...(Array.isArray(group.users) ? group.users : []),
    ];
    setEditGroup(group);
    setGroupForm({
      groupName: group.groupName || "",
      userIds: Array.from(
        new Set(selectedUsers.map((user) => normalizeId(user)).filter(Boolean))
      ),
    });
    setGroupUserDropdownOpen(false);
    setGroupModal(true);
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && <Toaster notification={snack.notification} onClose={() => setSnack(null)} />}

      <div className="card shadow-sm border-0 rounded-3 p-3 mb-3">
        <ListHeader
          title="Notifications"
          searchValue={query}
          searchPlaceholder="Search notifications..."
          onSearchChange={setQuery}
          limit={limit}
          onLimitChange={(size) => {
            setPageNo(1);
            setLimit(size);
          }}
        />

        {/* Bulk actions | Add Promo + Send */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          {/* Bulk actions only make sense when there are rows to select */}
          {filteredNotifications.length > 0 && (
            <>
              <span className="text-muted small">{selectedIds.length} selected</span>
              <button className="btn btn-sm btn-outline-danger" onClick={() => openDeleteNotifications(selectedIds)}>
                Delete
              </button>
            </>
          )}
          <div className="d-flex flex-wrap gap-2 ms-auto">
            <button className="btn btn-sm btn-outline-custom" onClick={() => setPromoModal(true)}>
              Add Promo
            </button>
            <button
              className="btn btn-sm btn-outline-custom"
              onClick={() => {
                setSendUserDropdownOpen(false);
                setSendGroupDropdownOpen(false);
                setSendModal(true);
              }}
            >
              Send
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    aria-label="Select all notifications on this page"
                  />
                </th>
                <th style={{ width: "60px" }}>Sr.</th>
                <th>Title</th>
                <th>Message</th>
                <th>Type</th>
                <th>User Type</th>
                <th style={{ width: "90px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredNotifications.length > 0 ? (
                filteredNotifications.map((item, index) => (
                  <tr key={item._id || index}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(item._id)}
                        onChange={() => toggleRowSelection(item._id)}
                        aria-label={`Select ${item.title || "notification"}`}
                      />
                    </td>
                    <td>{(pageNo - 1) * limit + index + 1}</td>
                    <td>{item.title || "-"}</td>
                    <td>{item.message || "-"}</td>
                    <td>{item.notificationType || "-"}</td>
                    <td>{item.userType || "-"}</td>
                    <td>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => {
                        setDeleteId(item._id);
                        setDeleteIds([]);
                      }}>
                        <CgTrash size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-4">
                    No notifications found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <ListPagination
          show={totalRecords > 0}
          pageNo={pageNo}
          totalPages={totalPages}
          onPageChange={setPageNo}
        />
      </div>

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Notification Groups</h4>
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() => {
              setEditGroup(null);
              setGroupForm({ groupName: "", userIds: [] });
              setGroupUserDropdownOpen(false);
              setGroupModal(true);
            }}
          >
            Add Group
          </button>
        </div>
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: "60px" }}>Sr.</th>
                <th>Group Name</th>
                <th>Users</th>
                <th style={{ width: "150px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.length > 0 ? (
                groups.map((group, index) => (
                  <tr key={group._id || index}>
                    <td>{index + 1}</td>
                    <td>{group.groupName || "-"}</td>
                    <td>{group.userCount ?? (Array.isArray(group.userIds) ? group.userIds.length : "-")}</td>
                    <td>
                      <button className="btn btn-sm btn-outline-custom me-2" onClick={() => openGroupEdit(group)}>
                        Edit
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => setDeleteGroupId(group._id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center text-muted py-4">
                    No groups found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {users.length > 0 && <small className="text-muted">Loaded users for group selection: {users.length}</small>}
      </div>

      <Modal show={promoModal} onHide={() => setPromoModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Promo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={submitPromo}>
            <div className="mb-3">
              <label className="form-label">Title</label>
              <input className="form-control" value={promoForm.title} onChange={(e) => setPromoForm((prev) => ({ ...prev, title: e.target.value }))} />
            </div>
            <div className="mb-3">
              <label className="form-label">Message</label>
              <textarea className="form-control" rows="3" value={promoForm.message} onChange={(e) => setPromoForm((prev) => ({ ...prev, message: e.target.value }))} />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setPromoModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-outline-custom">Save</button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal
        show={sendModal}
        onHide={() => {
          setSendModal(false);
          setSendUserDropdownOpen(false);
          setSendGroupDropdownOpen(false);
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Send Notification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={submitNotification}>
            {[
              ["title", "Title"],
              ["message", "Message"],
            ].map(([name, label]) => (
              <div className="mb-3" key={name}>
                <label className="form-label">{label}</label>
                <input className="form-control" value={sendForm[name]} onChange={(e) => setSendForm((prev) => ({ ...prev, [name]: e.target.value }))} />
              </div>
            ))}
            <div className="mb-3">
              <label className="form-label">Users</label>
              <div className="position-relative" ref={sendUserDropdownRef}>
                <button
                  type="button"
                  className="form-select text-start"
                  onClick={() => setSendUserDropdownOpen((open) => !open)}
                >
                  {sendForm.userIds.length
                    ? `${sendForm.userIds.length} user${sendForm.userIds.length > 1 ? "s" : ""} selected`
                    : "Select users"}
                </button>
                {sendUserDropdownOpen && (
                  <div
                    className="position-absolute bg-white border rounded shadow-sm w-100 mt-1 p-2"
                    style={{ zIndex: 1060, maxHeight: 260, overflowY: "auto" }}
                  >
                    {unselectedSendUsers.length > 0 ? (
                      unselectedSendUsers.map((user) => (
                        <button
                          key={getUserId(user)}
                          type="button"
                          className="btn btn-light text-start w-100 mb-1"
                          onClick={() => addSendUserId(getUserId(user))}
                        >
                          {getUserLabel(user)}
                        </button>
                      ))
                    ) : (
                      <div className="text-muted small px-2 py-2">
                        No users left to add
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-2">
                <div className="text-muted small mb-1">Selected Users</div>
                {selectedSendUsers.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {selectedSendUsers.map((user) => (
                      <span
                        key={user.id}
                        className="badge bg-light text-dark border"
                        role="button"
                        onClick={() => removeSendUserId(user.id)}
                        style={{ cursor: "pointer" }}
                      >
                        {user.label} <span className="ms-1">X</span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted small">No users selected</div>
                )}
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Groups</label>
              <div className="position-relative" ref={sendGroupDropdownRef}>
                <button
                  type="button"
                  className="form-select text-start"
                  onClick={() => setSendGroupDropdownOpen((open) => !open)}
                >
                  {sendForm.groupIds.length
                    ? `${sendForm.groupIds.length} group${sendForm.groupIds.length > 1 ? "s" : ""} selected`
                    : "Select groups"}
                </button>
                {sendGroupDropdownOpen && (
                  <div
                    className="position-absolute bg-white border rounded shadow-sm w-100 mt-1 p-2"
                    style={{ zIndex: 1060, maxHeight: 260, overflowY: "auto" }}
                  >
                    {unselectedSendGroups.length > 0 ? (
                      unselectedSendGroups.map((group) => (
                        <button
                          key={getGroupId(group)}
                          type="button"
                          className="btn btn-light text-start w-100 mb-1"
                          onClick={() => addSendGroupId(getGroupId(group))}
                        >
                          {getGroupLabel(group)}
                        </button>
                      ))
                    ) : (
                      <div className="text-muted small px-2 py-2">
                        No groups left to add
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-2">
                <div className="text-muted small mb-1">Selected Groups</div>
                {selectedSendGroups.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {selectedSendGroups.map((group) => (
                      <span
                        key={group.id}
                        className="badge bg-light text-dark border"
                        role="button"
                        onClick={() => removeSendGroupId(group.id)}
                        style={{ cursor: "pointer" }}
                      >
                        {group.label} <span className="ms-1">X</span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted small">No groups selected</div>
                )}
              </div>
            </div>
            <div className="row">
              {[
                ["notificationType", "Notification Type"],
                ["userType", "User Type"],
                ["sentType", "Sent Type"],
                ["messageType", "Message Type"],
                ["isStore", "Store"],
              ].map(([name, label]) => (
                <div className="col-md-4 mb-3" key={name}>
                  <label className="form-label">{label}</label>
                  <input type="number" className="form-control" value={sendForm[name]} onChange={(e) => setSendForm((prev) => ({ ...prev, [name]: e.target.value }))} />
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setSendModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-outline-custom">Send</button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal
        show={groupModal}
        onHide={() => {
          setGroupModal(false);
          setGroupUserDropdownOpen(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{editGroup ? "Edit Group" : "Add Group"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={submitGroup}>
            <div className="mb-3">
              <label className="form-label">Group Name</label>
              <input className="form-control" value={groupForm.groupName} onChange={(e) => setGroupForm((prev) => ({ ...prev, groupName: e.target.value }))} />
            </div>
            <div className="mb-3">
              <label className="form-label">Users</label>
              <div className="position-relative" ref={groupUserDropdownRef}>
                <button
                  type="button"
                  className="form-select text-start"
                  onClick={() => setGroupUserDropdownOpen((open) => !open)}
                >
                  {groupForm.userIds.length
                    ? `${groupForm.userIds.length} user${groupForm.userIds.length > 1 ? "s" : ""} selected`
                    : "Select users"}
                </button>
                {groupUserDropdownOpen && (
                  <div
                    className="position-absolute bg-white border rounded shadow-sm w-100 mt-1 p-2"
                    style={{ zIndex: 1060, maxHeight: 260, overflowY: "auto" }}
                  >
                    {unselectedGroupUsers.length > 0 ? (
                      unselectedGroupUsers.map((user) => (
                        <button
                          key={getUserId(user)}
                          type="button"
                          className="btn btn-light text-start w-100 mb-1"
                          onClick={() => addGroupUserId(getUserId(user))}
                        >
                          {getUserLabel(user)}
                        </button>
                      ))
                    ) : (
                      <div className="text-muted small px-2 py-2">
                        No users left to add
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-2">
                <div className="text-muted small mb-1">Selected Users</div>
                {selectedGroupUsers.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {selectedGroupUsers.map((user) => (
                      <span
                        key={user.id}
                        className="badge bg-light text-dark border"
                        role="button"
                        onClick={() => removeGroupUserId(user.id)}
                        style={{ cursor: "pointer" }}
                      >
                        {user.label} <span className="ms-1">X</span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted small">No users selected</div>
                )}
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setGroupModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-outline-custom">Save</button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <DeleteConfirmModal
        show={Boolean(deleteId) || deleteIds.length > 0}
        count={deleteIds.length || 1}
        singular="notification"
        plural="notifications"
        loading={loading}
        onConfirm={deleteNotification}
        onCancel={() => {
          setDeleteId(null);
          setDeleteIds([]);
        }}
      />
      <DeleteConfirmModal
        show={Boolean(deleteGroupId)}
        count={1}
        singular="notification group"
        plural="notification groups"
        loading={loading}
        onConfirm={deleteGroup}
        onCancel={() => setDeleteGroupId(null)}
      />
    </>
  );
};

export default NotificationsList;
